//src/components/ContactPointsPanel.tsx
// ============================================================================
// CONTACT POINTS MANAGEMENT PANEL
// ============================================================================
// This component manages user's contact points (phone, email, social handles)
// with a 2-gate verification system:
//   Gate 1: User confirms ownership ("Yes, this is mine")
//   Gate 2: Employee verifies via DM code
//
// Features:
// - Phone/Email OTP verification
// - Social handle verification via DM codes
// - Staging → Confirmed → Verified flow
// - Real-time verification status via AppSync subscriptions
// ============================================================================

import { useMemo, useState, useRef, useEffect, Fragment } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "../../amplify/data/resource";
import toast from 'react-hot-toast';
import {
  FaPhone,
  FaEnvelope,
  FaInstagram,
  FaFacebook,
  FaSnapchatGhost,
  FaLinkedin,
} from "react-icons/fa";
import { SiTiktok } from "react-icons/si";
import { BsThreeDots } from 'react-icons/bs';
import { VerifiedHandleRow } from './VerifiedHandleRow';
import { StagingHandleCard } from './StagingHandleCard';
import { PendingVerificationCard } from './PendingVerificationCard';
import { ConfirmModal } from './ConfirmModal';
import { HandleHelpModal } from './HandleHelpModal';
import { refreshUserPii } from './UserData';

const client = generateClient<Schema>();

// ============================================================================
// TYPES
// ============================================================================

export type CPItem = {
  kind: string;
  value: string;
  status: "staging" | "pending" | "verified";
  code?: string;
  otp?: string;
  
  // 2-gate verification fields
  confirmedByUser?: boolean;
  verificationCode?: string;
  verified?: boolean;
  contactPointId?: string;
  
  // Match pending fields
  matchPending?: boolean;
  matchPendingAt?: string;
};

type Props = {
  initialItems: CPItem[];
  onSave: (items: CPItem[]) => Promise<void>;
  dmLinks?: Partial<Record<string, string>>;
  primaryEmail?: string;
  primaryPhone?: string;
  userTier?: string;
  // Optional setter for the parent's user state. Provided so this panel can
  // call refreshUserPii after success paths that change vault/PII state.
  setUser?: (updater: (prev: any) => any) => void;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const socialKinds = new Set(["instagram", "tiktok", "snapchat", "facebook", "linkedin"]);

function isPrimaryContact(item: CPItem, primaryEmail?: string, primaryPhone?: string): boolean {
  if (item.kind === "email" && primaryEmail) {
    return normalizeEmail(item.value) === normalizeEmail(primaryEmail);
  }
  if (item.kind === "phone" && primaryPhone) {
    return normalizePhone(item.value) === normalizePhone(primaryPhone);
  }
  return false;
}

function digitsOnly(s: string) {
  return s.replace(/\D/g, "");
}

function formatUSPhone(d: string) {
  const n = digitsOnly(d).slice(0, 10);
  if (n.length < 4) return n;
  if (n.length < 7) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
}

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

const normalizePhone = (s: string) => {
  const d = (s || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  if (d.length === 10) return `+1${d}`;
  return "";
};

const normalizeEmail = (s: string) => (s || "").trim().toLowerCase();

function normalizeSocialHandle(kind: string, input: string): string {
  let cleaned = (input || "").trim();

  while (cleaned.startsWith('/')) {
    cleaned = cleaned.slice(1);
  }

  try {
    if (cleaned.includes("://") || cleaned.startsWith("www.")) {
      const url = new URL(cleaned.startsWith("http") ? cleaned : `https://${cleaned}`);
      let pathname = url.pathname.replace(/^\/+/, '');
      const segments = pathname.split('/').filter(Boolean);
      if (kind === 'snapchat') {
        // snapchat.com/add/username → username (last segment)
        pathname = segments[segments.length - 1] || cleaned;
      } else {
        pathname = segments[0] || cleaned;
      }
      pathname = pathname.split('?')[0].split('#')[0];
      pathname = pathname.startsWith('@') ? pathname.slice(1) : pathname;
      return (pathname || cleaned).toLowerCase();
    }
  } catch {
    // Fall through
  }

  cleaned = cleaned.startsWith("@") ? cleaned.slice(1) : cleaned;
  cleaned = cleaned.split('/')[0];
  cleaned = cleaned.split('?')[0];
  cleaned = cleaned.split('#')[0];

  return cleaned.toLowerCase();
}

function generateOtp() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

const keyFor = (i: CPItem) => `${i.kind}:${i.value.toLowerCase()}`;
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ContactPointsPanel({
  initialItems,
  onSave,
  primaryEmail,
  primaryPhone,
  userTier = 'tier1',
  setUser,
}: Props) {

  // Fire-and-forget PII refresh used after success paths that mutate vault
  // or User PII fields server-side. No-op if the parent didn't pass setUser.
  const triggerPiiRefresh = async () => {
    if (!setUser) return;
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;
      const rawBase = import.meta.env.VITE_API_BASE_URL as string;
      const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
      await refreshUserPii(apiBase, `Bearer ${idToken}`, setUser);
    } catch {
      // non-fatal — refresh will retry on next success path or bootstrap
    }
  };
  
  // ============================================================================
  // STATE
  // ============================================================================
  
  const [items, setItems] = useState<CPItem[]>(initialItems);

  useEffect(() => {
    setItems(initialItems.map(item => ({
      ...item,
      verified: item.verified !== undefined ? item.verified : item.status === 'verified',
      confirmedByUser: item.confirmedByUser !== undefined
        ? item.confirmedByUser
        : (item.status === 'verified' || item.status === 'pending'),
    })));
  }, [initialItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore pending confirmation state after app switch — sessionStorage survives
  // PWA backgrounding but clears when the browser session ends.
  useEffect(() => {
    const pending = sessionStorage.getItem('crushhh_pending_confirm');
    if (pending) {
      try {
        const { contactPointId } = JSON.parse(pending);
        const match = initialItems.find(i => i.contactPointId === contactPointId);
        if (match && !match.confirmedByUser && match.status !== 'verified') {
          setHandleToConfirm(match);
          setViewProfileModalOpen(true);
        } else {
          // Handle already confirmed/verified or no longer in staging — clear stale entry
          sessionStorage.removeItem('crushhh_pending_confirm');
        }
      } catch {
        sessionStorage.removeItem('crushhh_pending_confirm');
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll getUserPii every 30s to pick up server-side verification status
  // changes. The vault on the User row is the source of truth — ContactPoint
  // rows store ciphertext that the client can't decrypt anyway. Brief Q
  // replaces the direct AppSync ContactPoint reads with this poll.
  useEffect(() => {
    const interval = setInterval(() => {
      triggerPiiRefresh();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const [showConfirmed, setShowConfirmed] = useState(false);
  const [showStaging, setShowStaging] = useState(true);
  const [showPending, setShowPending] = useState(true);
  const [cpKind, setCpKind] = useState<string>("phone");
  const [cpValue, setCpValue] = useState<string>("");
  const [showVerified, setShowVerified] = useState(false);
  const [viewProfileModalOpen, setViewProfileModalOpen] = useState(false);
  const [helpModalItem, setHelpModalItem] = useState<CPItem | null>(null);
  // Brief AC — Loading state scoped to the modal's confirm button. Prevents
  // double-fire on the confirmUserHandle mutation; the modal disables both
  // buttons while this is true.
  const [confirming, setConfirming] = useState(false);
  const [handleToConfirm, setHandleToConfirm] = useState<CPItem | null>(null);
  const [showPendingVerification, setShowPendingVerification] = useState(true);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [addBtnHovered, setAddBtnHovered] = useState(false);
  const [addBtnPressed, setAddBtnPressed] = useState(false);
  const [removeHov,     setRemoveHov]     = useState<string | null>(null);
  const [removePress,   setRemovePress]   = useState<string | null>(null);

  const stagingSectionRef = useRef<HTMLDivElement | null>(null);
  const confirmedSectionRef = useRef<HTMLDivElement | null>(null);
  const pendingSectionRef = useRef<HTMLDivElement | null>(null);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // Phone/email OTP pending items (status === "pending", not yet OTP-verified)
  const pending = useMemo(() => 
    items.filter(i => i.status === "pending"),
  [items]);

  // Staging: social handles not yet confirmed by user — never phone/email
  const staging = useMemo(() => {
    return items.filter(i => {
      if (i.kind === 'phone' || i.kind === 'email') return false;
      return !(i.confirmedByUser ?? false) && !(i.verified ?? false);
    });
  }, [items]);

  // Confirmed: confirmed by user, not verified, free tier — social only.
  // Paid users skip this bucket entirely and go straight to pendingVerification.
  const confirmed = useMemo(() => {
    return items.filter(i => {
      if (i.kind === 'phone' || i.kind === 'email') return false;
      if (i.status === 'pending') return false;
      return (i.confirmedByUser ?? false) && !(i.verified ?? false) && userTier !== 'paid';
    });
  }, [items, userTier]);

  // Pending Verification: paid user with a confirmed-but-unverified handle.
  // Paid tier is the gate — matchPending is no longer used for bucket routing.
  const pendingVerification = useMemo(() => {
    return items.filter(i => {
      if (i.kind === 'phone' || i.kind === 'email') return false;
      if (i.status === 'pending') return false;
      return (i.confirmedByUser ?? false) && !(i.verified ?? false) && userTier === 'paid';
    });
  }, [items, userTier]);

  // Verified: phone/email always verified; social only if vault says verified
  const verified = useMemo(() => {
    return items.filter(i => {
      if (i.kind === 'phone' || i.kind === 'email') return true;
      return i.verified ?? false;
    });
  }, [items]);

  const socialHelp = socialKinds.has(cpKind);

  // ============================================================================
  // STYLING
  // ============================================================================

  const baseKindButton = "relative flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all";
  const selectedKindButton = "bg-brand text-white shadow-[2px_2px_0px_#D4724A]";
  const unselectedKindButton = "bg-[#E8D4B8] text-[#C96B6B] hover:bg-[#D4B896] focus:outline-none focus:ring-0 active:outline-none outline-none shadow-[2px_2px_0px_#D4724A]";

  // ============================================================================
  // PLATFORM CONFIGURATION
  // ============================================================================

  const allPlatforms = [
    { id: 'phone', icon: FaPhone, label: 'Phone' },
    { id: 'email', icon: FaEnvelope, label: 'Email' },
    { id: 'instagram', icon: FaInstagram, label: 'Instagram' },
    { id: 'facebook', icon: FaFacebook, label: 'Facebook' },
    { id: 'snapchat', icon: FaSnapchatGhost, label: 'Snapchat' },
    { id: 'tiktok', icon: SiTiktok, label: 'TikTok' },
    { id: 'linkedin', icon: FaLinkedin, label: 'LinkedIn' },
  ];

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handlePlatformSelect = (platformId: string) => {
    setCpKind(platformId);
    setShowPlatformModal(false);
  };

  // ============================================================================
  // OTP VERIFICATION
  // ============================================================================

  const verifyCode = async (item: CPItem, userCode: string) => {
    const trimmed = userCode.trim();
    if (!trimmed) return;

    if (item.kind === "phone" || item.kind === "email") {
      if (trimmed !== item.otp) {
        toast.error("Incorrect code. Check your text/email and try again.");
        return;
      }
      if (item.contactPointId) {
        try {
          const result = await client.mutations.verifyContactPointOTP({
            phone: item.value,
            otpCode: trimmed,
          });
          // AWSJSON arrives as a JSON-encoded string on the wire; parse defensively.
          let parsed: any = result.data;
          if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch { parsed = null; }
          }
          const response = parsed as { ok?: boolean; reason?: string } | null;
          if (!response?.ok) {
            toast.error(response?.reason || 'Failed to verify');
            return;
          }
          toast.success(`${item.kind === 'phone' ? 'Phone' : 'Email'} verified! ✅`);
          // Move to verified in local state immediately
          const updated = items.map(i =>
            keyFor(i) === keyFor(item)
              ? { ...i, status: 'verified' as const, verified: true, confirmedByUser: true }
              : i
          );
          setItems(updated);
          await onSave(updated);
          // Verification updated User.phone/phoneVerified server-side — pull fresh PII.
          await triggerPiiRefresh();
        } catch (error: any) {
          toast.error(error.message || 'Unknown error');
        }
      }
      return;
    }
  };

  // ============================================================================
  // ADD CONTACT POINT
  // ============================================================================

  const addPendingCP = async () => {
    let normalizedValue = "";
    let finalKind = cpKind;

    if (cpKind === "phone") {
      normalizedValue = normalizePhone(cpValue);
      if (!normalizedValue) {
        toast.error("Please enter a valid 10-digit phone number");
        return;
      }
      const alreadyExists = items.some(i => i.kind === "phone" && i.value === normalizedValue);
      if (alreadyExists) {
        toast.error("This phone number is already in your list");
        return;
      }
    } else if (cpKind === "email") {
      normalizedValue = normalizeEmail(cpValue);
      if (!isValidEmail(normalizedValue)) {
        toast.error("Please enter a valid email address");
        return;
      }
      const alreadyExists = items.some(i => i.kind === "email" && i.value === normalizedValue);
      if (alreadyExists) {
        toast.error("This email is already in your list");
        return;
      }
    } else if (socialKinds.has(cpKind)) {
      normalizedValue = normalizeSocialHandle(cpKind, cpValue);
      if (!normalizedValue) {
        toast.error(`Please enter a valid ${capitalize(cpKind)} handle`);
        return;
      }
      finalKind = "handle";
      const alreadyExists = items.some(
        i => i.kind === cpKind && i.value.toLowerCase() === normalizedValue.toLowerCase()
      );
      if (alreadyExists) {
        toast.error(`This ${capitalize(cpKind)} handle is already in your list`);
        return;
      }
    }

    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) {
        toast.error("Authentication required");
        return;
      }

      // ── PHONE / EMAIL: OTP flow ──────────────────────────────────────────
      if (cpKind === "phone" || cpKind === "email") {
        // Step 1: Create ContactPoint in DynamoDB
        let contactPointId: string | undefined;
        try {
          const result = await client.mutations.createContactPointWithVerification({
            kind: cpKind,
            platform: "none",   // ← must be non-empty string for GSI
            value: normalizedValue,
            valueNormalized: normalizedValue,
          });
          let response: any = result.data;
          if (typeof response === 'string') response = JSON.parse(response);
          if (typeof response === 'string') response = JSON.parse(response);
          if (response?.success) {
            contactPointId = response.contactPointId;
          }
        } catch (cpError) {
          void ['ContactPoint creation warning (non-fatal):', cpError];
        }

        // Step 2: Generate and send OTP
        const otp = generateOtp();
        try {
          if (cpKind === "phone") {
            await client.mutations.sendSms({
              to: normalizedValue,
              message: `Your Crushhh verification code is: ${otp}`,
            });
          } else {
            await client.mutations.sendEmail({
              to: normalizedValue,
              subject: "Verify your Crushhh email",
              message: `Your Crushhh verification code is: ${otp}`,
            });
          }
        } catch (e) {
          toast.error(`Failed to send verification code. Please try again.`);
          return;
        }

        // Step 3: Add to local state as pending
        const newItem: CPItem = {
          kind: cpKind,
          value: normalizedValue,
          status: "pending",
          otp,
          contactPointId,
          confirmedByUser: false,
          verified: false,
        };
        const updated = [...items, newItem];
        setItems(updated);
        await onSave(updated);
        // New pending ContactPoint exists server-side — pull fresh PII so the
        // contactPointsVault items[] in user state reflect it.
        await triggerPiiRefresh();
        setCpValue("");
        toast.success(`Verification code sent to your ${capitalize(cpKind)}!`);
        setTimeout(() => {
          pendingSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 100);
        return;
      }

      // ── SOCIAL HANDLES: staging flow ────────────────────────────────────
      const result = await client.mutations.createContactPointWithVerification({
        kind: finalKind,
        platform: socialKinds.has(cpKind) ? cpKind : "none",
        value: normalizedValue,
        valueNormalized: normalizedValue,
      });

      let response: any = result.data;
      if (!response) { toast.error("No response from server"); return; }
      if (typeof response === 'string') response = JSON.parse(response);
      if (typeof response === 'string') response = JSON.parse(response);

      if (!response.success) {
        toast.error(response.error || "Failed to add contact point");
        return;
      }

      const newItem: CPItem = {
        kind: socialKinds.has(cpKind) ? cpKind : finalKind,
        value: normalizedValue,
        status: "staging",
        verificationCode: response.verificationCode,
        contactPointId: response.contactPointId,
        confirmedByUser: false,
        verified: false,
      };

      const updated = [...items, newItem];
      setItems(updated);
      await onSave(updated);
      // Staged social handle written server-side — pull fresh PII so the
      // contactPointsVault items[] in user state reflect it.
      await triggerPiiRefresh();
      setCpValue("");
      toast.success(`${cpKind.charAt(0).toUpperCase() + cpKind.slice(1)} handle added! Confirm it's yours below.`);
      setTimeout(() => {
        stagingSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);

    } catch (error) {
      void ["Add contact point error:", error];
      toast.error("Failed to add contact point");
    }
  };

  const handleViewProfile = async (item: CPItem) => {
    const platform = item.kind;
    const handle = item.value.replace('@', '');
    const PLATFORM_URLS: Record<string, string> = {
      instagram: `https://instagram.com/${handle}`,
      snapchat: `https://snapchat.com/add/${handle}`,
      tiktok: `https://tiktok.com/@${handle}`,
      facebook: `https://facebook.com/${handle}`,
      linkedin: `https://linkedin.com/in/${handle}`,
    };
    const url = PLATFORM_URLS[platform];
    if (url) {
      window.open(url, '_blank');
      sessionStorage.setItem('crushhh_pending_confirm', JSON.stringify({
        contactPointId: item.contactPointId,
        value: item.value,
        kind: item.kind,
      }));
      setHandleToConfirm(item);
      setViewProfileModalOpen(true);
    }
  };

  const confirmStagingItem = async () => {
    if (!handleToConfirm?.contactPointId) {
      toast.error("Invalid contact point");
      return;
    }
    // Guard against double-fire — re-entry while a confirm is in flight is
    // a no-op. The button is also disabled via isLoading on the modal, but
    // the guard here is defense-in-depth for any non-button trigger path.
    if (confirming) return;
    setConfirming(true);
    try {
      const result = await client.mutations.confirmUserHandle({
        contactPointId: handleToConfirm.contactPointId,
      });
      const data = JSON.parse(result.data as string);
    if (data.success) {
        const updated = items.map(i =>
          keyFor(i) === keyFor(handleToConfirm)
            ? {
                ...i,
                confirmedByUser: true,
                verificationCode: data.verificationCode,
                matchPending: userTier === 'paid',
              }
            : i
        );
        setItems(updated);

        await onSave(updated);
        // Handle confirmation flipped confirmedByUser server-side and may
        // have updated matchPending / vault state — pull fresh PII.
        await triggerPiiRefresh();
        window.dispatchEvent(new CustomEvent('crushhh:contactConfirmed'));
        toast.success(`Handle confirmed! You'll be notified when someone adds you.`);
        setViewProfileModalOpen(false);
        setHandleToConfirm(null);
        sessionStorage.removeItem('crushhh_pending_confirm');
        setTimeout(() => {
          confirmedSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 100);
      } else {
        toast.error(data.error || "Failed to confirm handle");
      }
    } catch (error) {
      void ["Confirm staging error:", error];
      toast.error("Failed to confirm handle");
    } finally {
      // Always clear — covers success, server-failure, and thrown-exception paths.
      setConfirming(false);
    }
  };

  const removeStagingItem = async (item: CPItem) => {
    const updated = items.filter(i => keyFor(i) !== keyFor(item));
    setItems(updated);
    await onSave(updated);
    toast.success("Handle removed");
  };

  const removePendingItem = async (item: CPItem) => {
    const updated = items.filter(i => keyFor(i) !== keyFor(item));
    setItems(updated);
    await onSave(updated);
    toast.success("Contact point removed");
  };

  const removeVerifiedItem = async (item: CPItem) => {
    const updated = items.filter(i => keyFor(i) !== keyFor(item));
    setItems(updated);
    await onSave(updated);
    toast.success("Contact point removed");
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-4">
      <h2
        className="text-2xl text-gray-900"
        style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700 }}
      >
        Crushhh-IDs
      </h2>
      <p
        className="text-gray-700"
        style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 400 }}
      >
        How people add and match with you.
      </p>

      {/* ── VERIFIED ─────────────────────────────────────────────────────── */}
      {verified.length > 0 && (
        <details
          open={showVerified}
          onToggle={(e) => setShowVerified(e.currentTarget.open)}
          className="bg-transparent"
        >
          <summary className="cursor-pointer p-4 flex items-center justify-between rounded-t-lg list-none [&::-webkit-details-marker]:hidden">
            <span
              className="hover:text-gray-500 text-gray-900"
              style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 600 }}
            >
              Verified Crushhh-IDs ({verified.length})
            </span>
            <svg
              className={`w-6 h-6 text-gray-600 hover:text-gray-400 transition-transform duration-300 ${showVerified ? 'rotate-0' : 'rotate-90'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div className="p-4 pt-2 space-y-2 border-t border-gray-100">
            {verified.map((item) => (
              <VerifiedHandleRow
                key={keyFor(item)}
                kind={item.kind}
                value={item.value}
                isPrimary={isPrimaryContact(item, primaryEmail, primaryPhone)}
                onRemove={() => removeVerifiedItem(item)}
              />
            ))}
          </div>
        </details>
      )}

      {/* ── PENDING MATCH VERIFICATION (social handles, match detected) ───── */}
      {pendingVerification.length > 0 && (
        <details
          open={showPendingVerification}
          onToggle={(e) => setShowPendingVerification(e.currentTarget.open)}
          className="bg-transparent"
        >
          <summary className="cursor-pointer p-4 flex items-center justify-between rounded-t-lg list-none [&::-webkit-details-marker]:hidden">
            <span className="font-semibold hover:text-gray-400 text-gray-900">
              Pending Verification ({pendingVerification.length})
            </span>
            <svg
              className={`w-6 h-6 text-gray-600 hover:text-gray-400 transition-transform duration-300 ${showPendingVerification ? 'rotate-0' : 'rotate-90'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div className="p-4 pt-2 space-y-4">
            <div className="bg-branddark border-2 border-branddark rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="font-semibold text-white mb-2">Verify Handles to Unlock Matching</p>
                  <p className="text-sm text-white mb-2">
                    Send us a DM from each account below with your verification code.
                  </p>
                </div>
              </div>
            </div>

            {pendingVerification.map((item) => {
              const platform = item.kind;
              const handle = item.value;
              const key = keyFor(item);
              const code = item.verificationCode || 'N/A';
              const dmLinks: Record<string, string> = {
                instagram: 'https://ig.me/m/crushhhfun',
                tiktok: 'https://www.tiktok.com/@crushhhfun',
                snapchat: 'https://www.snapchat.com/add/crushhhfun',
                facebook: 'https://m.me/crushhhfun',
                linkedin: 'https://www.linkedin.com/messaging/compose/?recipient=crushhhfun',
              };
              return (
                <div key={key} className="bg-white border-2 border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand flex items-center justify-center">
                        <span className="text-white font-bold text-lg">{platform.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">@{handle}</div>
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "#E8D4B8",
                            color: "#1A1A1A",
                            borderRadius: 100,
                            padding: "2px 10px",
                            fontSize: 12,
                            fontFamily: "'Nunito', sans-serif",
                            fontWeight: 700,
                          }}
                        >
                          {platform}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removePendingItem(item)}
                      onMouseEnter={() => setRemoveHov(key)}
                      onMouseLeave={() => { setRemoveHov(null); setRemovePress(null); }}
                      onMouseDown={() => setRemovePress(key)}
                      onMouseUp={() => setRemovePress(null)}
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                        background: removePress === key ? "rgba(0,0,0,0.12)" : removeHov === key ? "rgba(0,0,0,0.06)" : "#FFFFFF",
                        color: "#1A1A1A",
                        border: "3px solid #1A1A1A",
                        borderRadius: 100,
                        boxShadow: "4px 4px 0px #1A1A1A",
                        padding: "4px 14px",
                        cursor: "pointer",
                        transform: removePress === key ? "scale(0.97)" : "scale(1)",
                        transition: "all 0.1s ease",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-600 mb-1.5">Your Verification Code</div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-gray-100 px-3 py-2.5 rounded-lg font-mono text-base font-bold text-gray-900 border-2 border-gray-300">
                          {code}
                        </code>
                        <button
                          onClick={() => { navigator.clipboard.writeText(code); toast.success('Code copied!'); }}
                          className="px-3 py-2.5 bg-brand text-white rounded-lg hover:bg-branddark transition-colors text-sm font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-600 mb-1.5">Send Verification DM</div>
                      <a
                        href={dmLinks[platform] || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-gradient-to-r from-brand to-branddark text-white px-4 py-2.5 rounded-lg hover:shadow-lg transition-all text-center font-semibold"
                      >
                        DM @crushhh on {capitalize(platform)}
                      </a>
                      <div className="text-xs text-gray-500 mt-1.5 text-center">Include your code in the message</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      )}

      {/* ── CONFIRMED HANDLES (social, confirmed, no match yet) ────────────── */}
      {confirmed.length > 0 && (
        <details
          open={showConfirmed}
          onToggle={(e) => setShowConfirmed(e.currentTarget.open)}
          className="bg-transparent"
        >
          <summary className="cursor-pointer p-4 flex items-center justify-between rounded-t-lg list-none [&::-webkit-details-marker]:hidden">
            <span
              className="hover:text-gray-400 text-gray-900"
              style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700 }}
            >
              Confirmed Handles ({confirmed.length})
            </span>
            <svg
              className={`w-6 h-6 text-gray-600 hover:text-gray-400 transition-transform duration-300 ${showConfirmed ? 'rotate-0' : 'rotate-90'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div ref={confirmedSectionRef} className="p-4 pt-2 space-y-4">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl text-branddark">✓</span>
                <div className="flex-1">
                  <p
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      fontWeight: 700,
                      color: "#E05A3A",
                    }}
                  >
                    You're In...
                  </p>
                  <p
                    className="text-sm text-gray-600"
                    style={{ fontFamily: "'Nunito', sans-serif" }}
                  >
                    Your secret crush counter will tick up when someone adds one of your confirmed or verified handles. When you unlock matching you'll be prompted to verify your handles.
                  </p>
                </div>
              </div>
            </div>
            {confirmed.map((item) => (
              <div
                key={keyFor(item)}
                className="animate-pulse-slow"
                style={{
                  border: "1.5px solid rgba(26,26,26,0.1)",
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-branddark flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{item.kind.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <div
                        className="text-gray-900"
                        style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700 }}
                      >
                        @{item.value}
                      </div>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "#E8D4B8",
                          color: "#1A1A1A",
                          borderRadius: 100,
                          padding: "2px 10px",
                          fontSize: 12,
                          fontFamily: "'Nunito', sans-serif",
                          fontWeight: 700,
                        }}
                      >
                        {item.kind}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removePendingItem(item)}
                    onMouseEnter={() => setRemoveHov(keyFor(item))}
                    onMouseLeave={() => { setRemoveHov(null); setRemovePress(null); }}
                    onMouseDown={() => setRemovePress(keyFor(item))}
                    onMouseUp={() => setRemovePress(null)}
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      fontWeight: 600,
                      fontSize: 14,
                      background: removePress === keyFor(item) ? "rgba(0,0,0,0.12)" : removeHov === keyFor(item) ? "rgba(0,0,0,0.06)" : "#FFFFFF",
                      color: "#1A1A1A",
                      border: "3px solid #1A1A1A",
                      borderRadius: 100,
                      boxShadow: "4px 4px 0px #1A1A1A",
                      padding: "4px 14px",
                      cursor: "pointer",
                      transform: removePress === keyFor(item) ? "scale(0.97)" : "scale(1)",
                      transition: "all 0.1s ease",
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* ── OTP PENDING (phone/email awaiting code entry) ──────────────────── */}
      {pending.length > 0 && (
        <details
          open={showPending}
          onToggle={(e) => setShowPending(e.currentTarget.open)}
          className="bg-transparent"
        >
          <summary className="cursor-pointer p-4 flex items-center justify-between rounded-t-lg list-none [&::-webkit-details-marker]:hidden">
            <span className="font-semibold hover:text-gray-400 text-gray-900">
              Enter Verification Code ({pending.length})
            </span>
            <svg
              className={`w-6 h-6 text-gray-600 hover:text-gray-400 transition-transform duration-300 ${showPending ? 'rotate-0' : 'rotate-90'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div ref={pendingSectionRef} className="p-4 pt-2 space-y-3 border-t border-gray-100">
            {pending.map((item) => (
              <PendingVerificationCard
                key={keyFor(item)}
                item={item}
                onVerify={verifyCode}
                onRemove={removePendingItem}
                dmLinks={{}}
                devInline={false}
              />
            ))}
          </div>
        </details>
      )}

      {/* ── STAGING (social handles to confirm) ───────────────────────────── */}
      {staging.length > 0 && (
        <details
          open={showStaging}
          onToggle={(e) => setShowStaging(e.currentTarget.open)}
          className="bg-transparent"
        >
          <summary className="cursor-pointer p-4 flex items-center justify-between rounded-t-lg list-none [&::-webkit-details-marker]:hidden">
            <span
              className="hover:text-gray-400 text-gray-900"
              style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700 }}
            >
              Confirm Your Handle
            </span>
            <svg
              className={`w-6 h-6 text-gray-600 hover:text-gray-400 transition-transform duration-300 ${showStaging ? 'rotate-0' : 'rotate-90'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div ref={stagingSectionRef} className="p-4 pt-2 space-y-3 border-t border-gray-100">
            <p
              className="text-sm text-gray-600 mb-3"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Click "View &amp; Confirm" to verify each handle before it can be used for matching.
            </p>
            {staging.map((item) => (
              <Fragment key={keyFor(item)}>
                <StagingHandleCard
                  item={item}
                  onConfirm={() => handleViewProfile(item)}
                  onRemove={() => removeStagingItem(item)}
                />
                <button
                  onClick={() => setHelpModalItem(item)}
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 6,
                    marginBottom: 4,
                    padding: "4px 0",
                    background: "none",
                    border: "none",
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#888",
                    cursor: "pointer",
                    textDecoration: "underline",
                    textUnderlineOffset: 3,
                  }}
                >
                  Having trouble viewing their profile?
                </button>
              </Fragment>
            ))}
          </div>
        </details>
      )}

      {/* ── ADD CRUSHHH-ID ────────────────────────────────────────────────── */}
      <div className="space-y-3 pt-4 border-t-2 border-gray-300">
        <h3
          className="block text-gray-800 text-lg"
          style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 600 }}
        >
          Add Crushhh-ID
        </h3>

        {/* DESKTOP: Show all 7 buttons */}
        <div className="hidden sm:grid grid-cols-4 gap-2">
          {allPlatforms.map(platform => {
            const Icon = platform.icon;
            const isLinkedIn = platform.id === 'linkedin';
            return (
              <button
                key={platform.id}
                onClick={() => setCpKind(platform.id)}
                title={platform.label}
                className={`${baseKindButton} ${cpKind === platform.id ? selectedKindButton : unselectedKindButton}`}
                style={isLinkedIn ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none', flexDirection: 'column', gap: 2 } : {}}
              >
                <Icon size={24} />
                {isLinkedIn && (
                  <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 10, color: "#1A1A1A" }}>
                    Coming soon
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* MOBILE: Show 5 buttons + More button */}
        <div className="grid grid-cols-3 gap-2 sm:hidden">
          {allPlatforms.slice(0, 5).map(platform => {
            const Icon = platform.icon;
            return (
              <button
                key={platform.id}
                onClick={() => setCpKind(platform.id)}
                title={platform.label}
                className={`${baseKindButton} ${cpKind === platform.id ? selectedKindButton : unselectedKindButton}`}
              >
                <Icon size={24} />
              </button>
            );
          })}
          <button
            onClick={() => setShowPlatformModal(true)}
            title="More platforms"
            className={`${baseKindButton} ${unselectedKindButton}`}
          >
            <BsThreeDots size={24} />
          </button>
        </div>

        {/* Input field */}
        <>
          {(cpKind === "phone" || cpKind === "email") && (
            <p
              className="text-sm text-gray-800 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Additional phone and email verification coming soon.
            </p>
          )}
          <div className="flex flex-col gap-2">
            <input
              type="text"
              value={cpValue}
                onChange={(e) => {
                  if (cpKind === "phone") {
                    setCpValue(formatUSPhone(e.target.value));
                  } else if (socialKinds.has(cpKind)) {
                    setCpValue(e.target.value.toLowerCase());
                  } else {
                    setCpValue(e.target.value);
                  }
                }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPendingCP(); } }}
                placeholder={
                  cpKind === "phone" ? "(555) 555-5555" :
                  cpKind === "email" ? "email@example.com" :
                  socialKinds.has(cpKind) ? `${cpKind}handle` : ""
                }
                disabled={cpKind === "phone" || cpKind === "email"}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              />
              <button
                onClick={addPendingCP}
                disabled={!cpValue.trim() || cpKind === "phone" || cpKind === "email"}
                onMouseEnter={() => setAddBtnHovered(true)}
                onMouseLeave={() => { setAddBtnHovered(false); setAddBtnPressed(false); }}
                onMouseDown={() => setAddBtnPressed(true)}
                onMouseUp={() => setAddBtnPressed(false)}
                style={{
                  width: "100%",
                  padding: "12px 24px",
                  background: addBtnPressed ? "#B34428" : addBtnHovered ? "#C94E30" : "#E05A3A",
                  color: "#FFFFFF",
                  fontFamily: "'Fredoka', sans-serif",
                  fontWeight: 700,
                  fontSize: 18,
                  border: "3px solid #1A1A1A",
                  borderRadius: 100,
                  boxShadow: "4px 4px 0px #1A1A1A",
                  transform: addBtnPressed ? "scale(0.97)" : "scale(1)",
                  cursor: "pointer",
                  opacity: (!cpValue.trim() || cpKind === "phone" || cpKind === "email") ? 0.65 : 1,
                  transition: "all 0.1s ease",
                }}
              >
                Add
              </button>
            </div>
            {socialHelp && (
              <p
                className="text-sm text-gray-600 leading-relaxed"
                style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 600 }}
              >
                Enter your {cpKind} handle to get notified when someone adds you!
              </p>
            )}
          </>
      </div>

      {/* Platform Selection Modal (Mobile) */}
      {showPlatformModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl border-4 border-gray-950 max-w-sm w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3
                className="text-xl text-gray-600"
                style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700 }}
              >
                Choose Platform
              </h3>
              <button
                onClick={() => setShowPlatformModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {allPlatforms.map(platform => {
                const Icon = platform.icon;
                const isLinkedIn = platform.id === 'linkedin';
                return (
                  <button
                    key={platform.id}
                    onClick={() => handlePlatformSelect(platform.id)}
                    className={`${baseKindButton} ${cpKind === platform.id ? selectedKindButton : unselectedKindButton} flex-col gap-1`}
                    style={isLinkedIn ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
                  >
                    <Icon size={24} />
                    <span style={{ fontSize: 11, fontFamily: "'Fredoka', sans-serif" }}>{platform.label}</span>
                    {isLinkedIn && (
                      <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 9, color: "#1A1A1A" }}>
                        Coming soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Handle Modal */}
      <ConfirmModal
        isOpen={viewProfileModalOpen}
        onClose={() => { setViewProfileModalOpen(false); setHandleToConfirm(null); sessionStorage.removeItem('crushhh_pending_confirm'); }}
        onConfirm={confirmStagingItem}
        title="Confirm This Is Your Handle"
        message={`Did you verify that @${handleToConfirm?.value} is YOUR account?`}
        confirmText="Yes, Confirm"
        confirmButtonClass="bg-brand hover:bg-branddark"
        isLoading={confirming}
        loadingText="Confirming..."
      />

      {/* Handle Help Modal — diagnostic flow for staging handles */}
      {helpModalItem && (
        <HandleHelpModal
          platform={helpModalItem.kind}
          handle={helpModalItem.value}
          onConfirmed={() => {
            const item = helpModalItem;
            setHelpModalItem(null);
            setHandleToConfirm(item);
            setViewProfileModalOpen(true);
          }}
          onReEnter={() => setHelpModalItem(null)}
          onDismiss={() => setHelpModalItem(null)}
        />
      )}
    </div>
  );
}